/**
 * One place, today (`/b/:brandId/p/:placeId`). Read-only but for scheduling,
 * changing or cancelling an event (below); the room now re-reads
 * every 15 s while the tab is visible; the place's seasons are read once and
 * shared by how the last events went, the members (whose lists are read only
 * when opened), the standings (the tables after the first read when opened)
 * and the schedule (its staff-only groups read when staff open it); the
 * place's games are read once. pop.in staff on a dev or preview build can
 * schedule an event here, and change or cancel one coming up
 * (`canScheduleEvents`); Next up is read again after each.
 *
 * Staff reach every place from any brand's page, so for them the breadcrumb
 * names the place's own brand (or "House place"), never the brand selected in
 * the sidebar.
 */
import { ArrowLeft, ChevronRight } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";

import { placesPath } from "@/features/portal/navigation";
import { fixtureQuery } from "@/features/places/fixtures";
import { ownerLabel, PlaceToday } from "@/features/places/place-today";
import { usePortal } from "@/features/portal/portal-context";
import { PlacesError, PlacesLoading } from "@/features/places/places-page";
import {
  usePlace,
  usePlaceEvents,
  usePlaceGames,
  usePlaceMembers,
  usePlaceSchedule,
  usePlaceSeasons,
  usePlaceStandings,
} from "@/features/places/use-places";
import { canScheduleEvents } from "@/features/places/schedule-event";
import { canEditBrandGames } from "@/features/games/brand-games";
import { useScheduleEvent } from "@/features/places/use-schedule-event";
import { Card } from "@/components/ui/card";

export function PlacePage() {
  // The URL is the source of truth for the brand (see `navigation.ts`).
  const { brandId = "", placeId = "" } = useParams();
  const carry = fixtureQuery(useLocation().search);
  const load = usePlace(placeId);
  const { isSuperAdmin, brands } = usePortal();
  const place = load.state === "ready" ? load.data : null;
  // Whether this viewer can change the place's brand's games (its Games page, unit 8).
  const gamesEditable =
    !!place?.brand && canEditBrandGames({ isSuperAdmin, viewerRole: brands.find((b) => b.id === place.brand?.id)?.viewerRole });
  // One seasons read per visit; events, members, standings and the schedule fan out from it.
  const seasons = usePlaceSeasons(placeId, place !== null);
  const events = usePlaceEvents(placeId, seasons);
  const [members, showMembers] = usePlaceMembers(placeId, seasons);
  const [standings, showStandings] = usePlaceStandings(placeId, seasons);
  const games = usePlaceGames(placeId, place !== null);
  const [schedule, showSchedule, rereadNextUp] = usePlaceSchedule(placeId, seasons);
  const scheduling = useScheduleEvent(placeId, rereadNextUp, schedule);

  return (
    <div className="flex flex-col gap-4">
      <nav className="flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground">
        <Link to={placesPath(brandId) + carry} className="inline-flex items-center gap-1.5 hover:text-foreground">
          <ArrowLeft className="size-3.5" /> {isSuperAdmin ? "All places" : "Places"}
        </Link>
        {isSuperAdmin && place && (
          <>
            <ChevronRight className="size-3.5 text-text-3" />
            <span>{ownerLabel(place)}</span>
            <ChevronRight className="size-3.5 text-text-3" />
            <span className="text-foreground">{place.name}</span>
          </>
        )}
      </nav>
      {load.state === "loading" && <PlacesLoading what="the place" />}
      {load.state === "error" && <PlacesError message={load.message} />}
      {load.state === "ready" &&
        (load.data ? (
          <PlaceToday
            place={load.data}
            events={events}
            members={members}
            onShowMembers={showMembers}
            standings={standings}
            onShowStandings={showStandings}
            games={games}
            gamesEditable={gamesEditable}
            schedule={schedule}
            onShowSchedule={showSchedule}
            scheduling={canScheduleEvents({ isSuperAdmin }) ? { ...scheduling, seasons } : undefined}
            sample={load.sample}
            freshness={load.readAt === null ? undefined : { readAt: load.readAt, refreshFailed: load.refreshFailed }}
          />
        ) : (
          <Card className="p-5 text-[13px] text-muted-foreground">
            This place doesn't exist, or you don't have access to it.
          </Card>
        ))}
    </div>
  );
}
